import clsx from "clsx";
import Image from 'next/image';

import CategoryTokens from "@components/CategoryTokens";
import { ArrowRight } from "@components/Icons";
import Target from "@components/Target";
import fallbackImage from "@images/abstract/floor_dark_green_1.FINAL.jpg";
import { getAllAttackCategories } from "@lib/getAllAttackCategories";
import dayjs from "dayjs";

const EntryList = ({
	readMoreText = "Read More",
	heading = null,
	entries,
	smallThumb = false,
	hideImage = false,
	hideCategory = false,
	showSummary = false,
	largeHeading = false,
	showReadMore = false,
	toggleLinks = false,
	hrPosition = "bottom",
	buttonStyle = "transparent",
	hideSubject = false,
	hideDate = false,
	className = "",
	borderColor = "",
	buttonArrow = "right",
}) => {
	return (
		<div className={clsx("EntryList", className)}>
			{heading && (
				<h4
					className={clsx("EntryList__heading font-medium", "text-2xl", "mb-5")}
				>
					{heading}
				</h4>
			)}

			<div className={clsx("EntryList__entries")}>
				{entries.map((entry, index) => {
					const image = entry.image && entry.image[0];
					const imageBlur = entry.imageBlur && entry.imageBlur[0];

					const [categories, mainCount] = getAllAttackCategories(entry);

					const parsedDate = dayjs(entry.attackDate);

					return (
						<div
							key={entry.id}
							className={clsx(
								"EntryList__entry group",
								"mb-7.5 last-of-type:mb-0"
							)}
						>
							{!hideImage && !!image && !!image.url && (
								<Target
									target={{ element: entry, type: "entry" }}
									className={clsx(
										"EntryList__image",
										"w-32",
										{
											"w-24": smallThumb,
										},
										"flex-shrink-0",
										"mr-4"
									)}
								>
									<Image
										src={image.url}
										alt={image.title}
										width={image.width}
										height={image.height}
										layout="responsive"
										placeholder="blur"
										blurDataURL={imageBlur.url}
									/>
									{!image && (
										<div
											className={clsx(
												"EntryThumbnail__fallbackImage",
												"aspect-w-16",
												"aspect-h-16"
											)}
										>
											<Image
												src={fallbackImage}
												alt={heading}
												layout="fill"
												objectFit="cover"
												draggable={false}
												className={clsx("EntryThumbnail__image--fallback")}
											/>
										</div>
									)}
								</Target>
							)}
							<div className={clsx("EntryList__caption", "")}>
								{hrPosition === "top" && (
									<div
										className="border mt-7.5 mb-5"
										style={{ borderColor: borderColor }}
									/>
								)}
								<h5
									className={clsx("EntryList__heading font-medium mb-3", {
										"text-xl": !largeHeading,
										"text-xl lg:text-2xl": largeHeading,
										"group-hover:underline":
											!toggleLinks || entry.contentSections?.length > 0,
									})}
								>
									<Target target={{ element: entry, type: "entry" }}>
										{entry.title}
									</Target>
								</h5>

								{entry.messageSubject && !hideSubject && (
									<div className="EntryList__messageSubject font-medium text-md mb-7.5">
										Subject: “{entry.messageSubject}”
									</div>
								)}

								{showSummary && entry.summary && (
									<p className="EntryList__summary mb-7.5">{entry.summary}</p>
								)}

								{entry.attackDate && !hideDate && (
									<div className="EntryList__attackDate font-medium text-xs mb-7.5 text-gray-02">
										Attack Date: {parsedDate.format("MMMM D, YYYY")}
									</div>
								)}

								{categories.length > 0 && !hideCategory && (
									<div className={clsx("mb-10")}>
										<CategoryTokens
											categories={categories}
											mainCount={mainCount}
										/>
									</div>
								)}

								{showReadMore && (
									<div
										className={clsx("ButtonContainer", {
											"inline-block": buttonStyle === "solid",
										})}
									>
										<Target
											target={{ element: entry, type: "entry" }}
											className={clsx(
												"EntryThumbnail__target",
												"flex items-center",
												"group-hover:underline",
												{
													"bg-purple-01 text-white-01 shrink px-5 py-5 h-10":
														buttonStyle === "solid",
													"font-medium md:pl-0 lg:pr-7.5 pt-3":
														buttonStyle === "transparent",
												}
											)}
										>
											<span className={clsx("inline-block pb-[2px]")}>
												{readMoreText}
											</span>
											<div
												className={clsx(
													"EntryThumbnail__targetIcon",
													"ml-4",
													"transition-transform group-hover:translate-x-1",
													{
														"text-purple-01": buttonStyle === "transparent",
														"whitespace-nowrap": buttonStyle === "solid",
													}
												)}
											>
												<ArrowRight />
											</div>
										</Target>
									</div>
								)}
								{index + 1 != entries.length && hrPosition !== "top" && (
									<div
										className="border mt-15 mb-5"
										style={{ borderColor: borderColor }}
									/>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default EntryList;
